// ─── colour helpers ───────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r, g, b) {
  return (
    '#' +
    [r, g, b]
      .map((v) =>
        Math.max(0, Math.min(255, Math.round(v)))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  );
}

/** Blend a colour toward white. amount: 0 = original, 1 = pure white */
function lighten(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount
  );
}

/** Blend a colour toward black. amount: 0 = original, 1 = black */
function darken(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

/** Mix two hex colours. t: 0 = hex1, 1 = hex2 */
function mix(hex1, hex2, t) {
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  return rgbToHex(
    r1 + (r2 - r1) * t,
    g1 + (g2 - g1) * t,
    b1 + (b2 - b1) * t
  );
}

/** Return "r, g, b" string for use inside rgba() */
function rgb(hex) {
  return hexToRgb(hex).join(', ');
}

// ─── main ─────────────────────────────────────────────────────────────────────

/**
 * Derives every CSS custom-property from 4 brand colours and writes them to
 * the document root. Call once before the React tree renders.
 *
 * @param {{ primary: string, light: string, dark: string, cream: string }} palette
 */
export function applyTheme({ primary, light, dark, cream, headerBg, headerText }) {
  const textMain = darken(dark, 0.72);
  const textMuted = mix(dark, '#888888', 0.45);

  const vars = {
    // ── Brand palette ─────────────────────────────────────────────────────
    '--color-primary':       primary,
    '--color-primary-light': light,
    '--color-primary-dark':  dark,
    '--color-cream':         cream,

    // ── RGB channels (for rgba() opacity variants) ─────────────────────────
    '--color-primary-rgb':       rgb(primary),
    '--color-primary-light-rgb': rgb(light),
    '--color-primary-dark-rgb':  rgb(dark),
    '--text-main-rgb':           rgb(textMain),

    // ── Backgrounds ───────────────────────────────────────────────────────
    '--bg':             lighten(light, 0.85),
    '--bg-elevated':    '#ffffff',
    '--bg-tint':        lighten(light, 0.78),
    '--bg-tint-medium': lighten(light, 0.65),
    '--bg-warm-pop':    lighten(primary, 0.72),
    '--card-bg':        '#ffffff',
    '--input-bg':       lighten(light, 0.85),

    // ── Semantic accents ──────────────────────────────────────────────────
    '--accent':           primary,
    '--accent-soft':      `rgba(${rgb(primary)}, 0.08)`,
    '--accent-secondary': light,
    '--accent-dark':      dark,

    // ── Text ──────────────────────────────────────────────────────────────
    '--text-main':  textMain,
    '--text-muted': textMuted,

    // ── Borders / shadows ─────────────────────────────────────────────────
    '--border-subtle': `rgba(${rgb(dark)}, 0.18)`,

    // ── Header gradient ───────────────────────────────────────────────────
    //    If headerBg is set, use a flat solid colour; otherwise derive gradient.
    '--header-from':  headerBg ?? darken(primary, 0.70),
    '--header-via1':  headerBg ?? darken(primary, 0.40),
    '--header-via2':  headerBg ?? primary,
    '--header-via3':  headerBg ?? mix(primary, light, 0.5),
    '--header-to':    headerBg ?? light,
    '--color-on-dark': headerText ?? '#ffffff',

    // ── Danger (fixed — not theme-dependent) ──────────────────────────────
    '--color-danger':        '#dc2626',
    '--color-danger-soft':   '#fee2e2',
    '--color-danger-border': '#fca5a5',

    // ── Shape (fixed) ─────────────────────────────────────────────────────
    '--radius-lg': '18px',
    '--radius-xl': '26px',
  };

  const root = document.documentElement;
  for (const [prop, value] of Object.entries(vars)) {
    root.style.setProperty(prop, value);
  }
}
